import { action, computed, observable } from "mobx";
import { ObservableSubscription } from "@apollo/client";
import { store } from "@store";
import { ObservableGraphqlQuery } from "@clientUtils";
import { ReminderFormData } from "@containers/ReminderDialog/ReminderStore";
import {
  CustomerEventFilter,
  CustomerEventLog_AddCustomerComment,
  CustomerEventLog_GetEvents,
  CustomerEventLog_Delete as deleteEvent,
  CustomerEventLog_Update as updateEvent,
  CustomerEventLog_AddCustomerReminder,
  CustomerEventLog_AddCustomerMetInPerson
} from "@graphql";
import { CommentBoxData } from "../CommentBox/CommentBoxStore";
import { debounce } from "lodash";

export type CustomerCommentEventMeta = Pick<
  CustomerEventLog_AddCustomerComment.Variables["input"],
  "ContactPersonId" | "RootCustomerId" | "AreasOfExpertise"
>;

export class CustomerEventLogStore {
  @observable textFilter = "";

  private _query = new ObservableGraphqlQuery(() =>
    CustomerEventLog_GetEvents.funcWatch(
      this.initialVars,
      { fetchPolicy: "network-only" } // or cacheRedirects is needed
    )
  );

  private subscription: ObservableSubscription;

  constructor(
    private customerId: string,
    private customerGroup: string | null,
    private initialVars: CustomerEventLog_GetEvents.Variables
  ) {}

  @computed get error() {
    return this._query.error;
  }

  @computed get loading() {
    return this._query.loading;
  }

  @computed get eventsSorted() {
    if (!this._query.data?.customerEvents) {
      return [];
    }

    return this._query.data.customerEvents
      .sortBy((x) => !x.Sticky)
      .filter((ev) => !ev.Deleted);
  }

  unsubscribe() {
    this.subscription && this.subscription.unsubscribe();
  }

  refetch = debounce((filter?: CustomerEventFilter) => {
    this._query.refetch(filter ? { filter } : undefined);
  }, 500);

  @action.bound addCustomerComment(
    data: CommentBoxData,
    meta: CustomerCommentEventMeta,
    reminder?: ReminderFormData
  ) {
    const withTodo = reminder ? { ...reminder, tags: ["Kund"] } : null;

    return CustomerEventLog_AddCustomerComment.func({
      input: {
        ...meta,
        CustomerGroup: this.customerGroup,
        CustomerId: this.customerId,
        Description: data.text
      },
      withTodo
    })
      .then(() => this.refetch())
      .catch((e) => {
        store().snackbars.error(`Cannot add comment.\nReason: ${e}`);
        console.error(e);
      });
  }

  @action.bound addReminder(data: CommentBoxData, meta: CustomerCommentEventMeta) {
    return CustomerEventLog_AddCustomerReminder.func({
      input: {
        ...meta,
        CustomerGroup: this.customerGroup,
        CustomerId: this.customerId,
        Description: data.text
      },
      withTodo: {
        date: data.remindOn,
        assignedUserId: data.assignTo ? data.assignTo._id : null,
        assignedTeam: data.assignTeam ? data.assignTeam : null,
        text: data.text,
        tags: [...data.tags, "Kund"],
        priority: data.priority
      }
    })
      .then(() => this.refetch())
      .catch((e) => {
        store().snackbars.error(`Cannot add comment.\nReason: ${e}`);
        console.error(e);
      });
  }

  @action.bound addMetInPerson(
    data: CommentBoxData,
    meta: CustomerCommentEventMeta,
    reminder?: ReminderFormData
  ) {
    return CustomerEventLog_AddCustomerMetInPerson.func({
      input: {
        ...meta,
        CustomerGroup: this.customerGroup,
        Description: data.text,
        CustomerId: this.customerId
      },
      withTodo: reminder
    })
      .then(() => this.refetch())
      .catch((e) => {
        store().snackbars.error(`Cannot add comment.\nReason: ${e}`);
        console.error(e);
      });
  }

  deleteEvent = (eventId: string) => {
    deleteEvent
      .func({ eventId })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot delete event.\nReason: ${err}`);
      });
  };

  updateEvent = (eventId: string, desc: string) => {
    return updateEvent
      .func({ eventId, desc })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot update event.\nReason: ${err}`);
      });
  };
}
